import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { platformService } from '@/services/platformService'

/** Enrolamiento de MFA/TOTP del admin de plataforma. */
export default function PlatformSecurityPage() {
    const [enabled, setEnabled] = useState<boolean | null>(null)
    const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null)
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        platformService.mfaStatus().then((s) => setEnabled(s.mfaEnabled)).catch(() => setEnabled(false))
    }, [])

    const startSetup = async () => {
        setBusy(true)
        try { setSetup(await platformService.mfaSetup()) }
        catch { toast.error('No se pudo iniciar el setup de MFA') }
        finally { setBusy(false) }
    }

    const enable = async (e: React.FormEvent) => {
        e.preventDefault()
        setBusy(true)
        try {
            await platformService.mfaEnable(code)
            toast.success('MFA activado')
            setEnabled(true); setSetup(null); setCode('')
        } catch {
            toast.error('Código inválido')
        } finally { setBusy(false) }
    }

    return (
        <div className="max-w-2xl">
            <h1 className="mb-6 text-2xl font-semibold">Seguridad · MFA</h1>

            {enabled === null ? (
                <div className="text-slate-400">Cargando…</div>
            ) : enabled ? (
                <div className="rounded-xl border border-emerald-800 bg-emerald-900/30 p-5 text-emerald-300">
                    ✅ MFA (TOTP) está <b>activo</b> en tu cuenta.
                </div>
            ) : !setup ? (
                <div className="rounded-xl border border-slate-800 bg-slate-800/50 p-5">
                    <p className="text-slate-300">El doble factor (TOTP) no está activo. Actívalo para proteger tu cuenta de administrador de sistema.</p>
                    <button onClick={startSetup} disabled={busy}
                        className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60">
                        {busy ? 'Generando…' : 'Configurar MFA'}
                    </button>
                </div>
            ) : (
                <div className="space-y-4 rounded-xl border border-slate-800 bg-slate-800/50 p-5">
                    <ol className="list-decimal space-y-2 pl-5 text-slate-300">
                        <li>Agrega esta cuenta a tu app (Google Authenticator, Authy…) escaneando el QR del enlace o ingresando el secreto manualmente.</li>
                        <li>Ingresa el código de 6 dígitos para confirmar.</li>
                    </ol>
                    <div className="rounded-md bg-slate-900 p-3 text-sm">
                        <div className="text-slate-400">Secreto (base32):</div>
                        <div className="select-all font-mono text-slate-100">{setup.secret}</div>
                        <div className="mt-2 text-slate-400">otpauth URI:</div>
                        <div className="select-all break-all font-mono text-xs text-slate-300">{setup.otpauthUri}</div>
                    </div>
                    <form onSubmit={enable} className="flex items-end gap-3">
                        <div>
                            <label className="mb-1 block text-sm text-slate-300">Código (6 dígitos)</label>
                            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6}
                                className="w-40 rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-center font-mono tracking-widest text-slate-100 outline-none focus:border-indigo-500" />
                        </div>
                        <button type="submit" disabled={busy}
                            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60">
                            Activar
                        </button>
                    </form>
                </div>
            )}
        </div>
    )
}
